import { useCallback, useEffect, useRef, useState } from 'react';
import { withWindow } from '@shared/utils/withWindow';

/** Hands the submission its release, to call only from a branch that leaves the host something to retry. */
type CheckoutSubmission = (releaseForRetry: () => void) => Promise<void>;

type CheckoutSubmitLatch = Readonly<{
  /** Folded into the paywall CTA's loading state alongside the checkout mutation's own. */
  isSubmittingCheckout: boolean;
  /** Runs `submit` unless one is already under way. */
  runCheckout: (submit: CheckoutSubmission) => Promise<void>;
}>;

/**
 * One checkout submission at a time, held from the click until the page leaves.
 *
 * Every paywall awaits a draft flush before `createCheckout` starts, so the mutation's own
 * `isCreatingCheckout` is false for the whole flush — a second click there starts a second flush,
 * and both reach `createCheckout` with competing Stripe sessions. The latch is not released on
 * success either: `createCheckout` assigns `location.href` and resolves, its loading state drops,
 * and the CTA would come back for the beat before the page actually goes.
 *
 * "Until the page leaves" has one exception: a page the browser restores from its back/forward
 * cache comes back exactly as it left, latch included, with nothing re-mounting to reset it. A host
 * who presses Back on Stripe would find the CTA spinning for good. `pageshow` with `persisted` is
 * that restore, so it releases.
 */
export const useCheckoutSubmitLatch = (): CheckoutSubmitLatch => {
  const [isSubmittingCheckout, setIsSubmittingCheckout] = useState(false);
  const isSubmittingRef = useRef(false);

  const release = useCallback(() => {
    isSubmittingRef.current = false;
    setIsSubmittingCheckout(false);
  }, []);

  useEffect(
    () =>
      withWindow(
        global => {
          const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) release();
          };
          global.addEventListener('pageshow', handlePageShow);
          return () => global.removeEventListener('pageshow', handlePageShow);
        },
        () => undefined,
      ),
    [release],
  );

  const runCheckout = useCallback(
    async (submit: CheckoutSubmission) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setIsSubmittingCheckout(true);

      try {
        await submit(release);
      } catch (error) {
        release();
        throw error;
      }
    },
    [release],
  );

  return { isSubmittingCheckout, runCheckout };
};
